import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtDate, fmtTime, fmtMoney, fmtErr, photoSrc } from "../../api";
import Layout from "../../components/Layout";
import { StatusBadge, getGeoAccurate, signalLabel } from "./Dashboard";
import { MapPin, Warning, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PhotoPicker } from "../../components/PhotoPicker";
import { ExpensePanel } from "../../components/ExpensePanel";
import { LogWorkRequest } from "../../components/LogWorkRequest";
import { LogIssue } from "../../components/LogIssue";

const VerifyBadge = ({ stamp }) => {
  if (!stamp) return null;
  const map = { verified: ["Location verified", "text-primary"], low_confidence: ["Low-confidence GPS", "text-amber-600"], out_of_range: ["Out of range", "text-destructive"], unverified: ["No GPS", "text-muted-foreground"], reported: ["Time reported by office", "text-muted-foreground"] };
  const [label, cls] = map[stamp.verification] || map.unverified;
  return <span data-testid={`verify-${stamp.verification}`} className={`text-xs font-bold ${cls}`}>{label} · {fmtTime(stamp.time)}</span>;
};

export default function VisitDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [subs, setSubs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showResched, setShowResched] = useState(false);
  const [resched, setResched] = useState({ date: "", reason: "" });
  const [showDispatch, setShowDispatch] = useState(false);
  const [showLogWork, setShowLogWork] = useState(false);
  const [showLogIssue, setShowLogIssue] = useState(false);
  const [mode, setMode] = useState("broadcast");
  const [picked, setPicked] = useState([]);
  const [minutes, setMinutes] = useState(120);
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [upsells, setUpsells] = useState([]);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [minutesOnSite, setMinutesOnSite] = useState("");
  const [sig, setSig] = useState(undefined);  // undefined = not attempted, null = no fix, number = accuracy m

  const load = useCallback(() => api.get(`/visits/${id}`).then((r) => setV(r.data)).catch((e) => toast.error(fmtErr(e))), [id]);
  useEffect(() => {
    load();
    api.get("/subcontractors").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only bootstrap + polling; load is stable (useCallback [id])
  }, [load]);

  if (!v) return <Layout title="Job sheet"><p className="text-muted-foreground">Loading…</p></Layout>;

  const act = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(fmtErr(e)); } };
  const geoAction = (path, msg) => act(async () => {
    const g = await getGeoAccurate();
    setSig(g ? g.accuracy : null);
    await api.post(`/visits/${id}/${path}`, g || {});
  }, msg);

  const setPinHere = () => act(async () => {
    const g = await getGeoAccurate();
    if (!g) throw new Error("No GPS fix — move into the open and try again");
    await api.post(`/properties/${v.property_id}/pin`, { lat: g.lat, lng: g.lng });
  }, "Pin set to where you're standing — future check-ins verify against it");

  const dispatch = () => act(async () => {
    await api.post(`/visits/${id}/dispatch`, { mode, subcontractor_ids: picked, response_minutes: Number(minutes) });
    setShowDispatch(false);
  }, "Offers sent");

  const complete = () => act(async () => {
    const g = await getGeoAccurate(3000);
    await api.post(`/visits/${id}/complete`, { notes, photos, addon_lines: upsells, ...(g || {}) });
  }, "Job complete");

  const cancelJob = () => act(async () => {
    await api.post(`/visits/${id}/cancel`, { reason: cancelReason.trim() });
    setShowCancel(false); setCancelReason("");
  }, "Job cancelled — assigned crew notified by text");

  const completeForCrew = () => act(async () => {
    await api.post(`/visits/${id}/complete`, { notes, photos, addon_lines: upsells, minutes_on_site: Number(minutesOnSite) || undefined });
    setMinutesOnSite("");
  }, "Job completed on behalf of crew");

  const assignDirect = () => act(async () => {
    await api.post(`/visits/${id}/assign`, { subcontractor_id: picked[0] });
    setPicked([]); setShowDispatch(false);
  }, "Assigned directly — crew member notified, no offer needed");

  const cancellable = ["scheduled", "assigned", "in_progress", "blocked"].includes(v.status);

  const workable = ["scheduled", "assigned", "in_progress", "blocked"].includes(v.status);

  return (
    <Layout title={v.property_name}>
      <div className="space-y-8">
        <div className="card-flat p-6">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-heading text-xl font-bold" data-testid="visit-property">{v.property_name}</p>
              <p className="text-sm text-muted-foreground">{v.address}</p>
              <p className="text-sm text-muted-foreground">{v.owner_name} · {fmtDate(v.date)}{v.driveway_size ? ` · Driveway: ${v.driveway_size}` : ""}</p>
              {v.delayed_from && <p className="mt-1 text-xs font-bold text-[hsl(196,72%,46%)]" data-testid="delayed-note">Delayed from {fmtDate(v.delayed_from)} ({v.delay_reason || "weather"})</p>}
              {v.rescheduled_from && <p className="mt-1 text-xs font-bold text-[hsl(196,72%,46%)]" data-testid="rescheduled-note">Rescheduled from {fmtDate(v.rescheduled_from)} ({v.reschedule_reason || "schedule change"})</p>}
            </div>
            <StatusBadge s={v.status} />
          </div>
          {v.access_notes && <p className="mt-3 rounded-md bg-muted p-3 text-sm"><span className="font-bold">Access:</span> {v.access_notes}</p>}
        </div>

        {v.reschedule_request && (
          <div className="card-flat space-y-3 border-l-4 border-l-[hsl(43,74%,50%)] p-4" data-testid="reschedule-request">
            <p className="text-sm font-semibold">{v.reschedule_request.sub_name} asked to move this job to <strong>{fmtDate(v.reschedule_request.date)}</strong></p>
            {v.reschedule_request.reason && <p className="text-xs text-muted-foreground">{v.reschedule_request.reason}</p>}
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="reschedule-approve" onClick={() => act(() => api.post(`/visits/${id}/reschedule-decide`, { approve: true }), "Moved — crew notified")} className="h-12 rounded-md bg-primary font-bold text-primary-foreground">Approve move</button>
              <button data-testid="reschedule-decline" onClick={() => act(() => api.post(`/visits/${id}/reschedule-decide`, { approve: false }), "Declined — crew notified")} className="h-12 rounded-md border border-destructive font-bold text-destructive">Decline</button>
            </div>
          </div>
        )}

        {v.status === "blocked" && (
          <div className="card-flat space-y-3 border-l-4 border-l-[hsl(24,90%,42%)] p-4" data-testid="blocked-banner">
            <p className="text-sm font-semibold">Blocked on site{v.blocked_by ? ` — reported by ${v.blocked_by}` : ""}</p>
            <p className="text-xs text-muted-foreground">{v.blocked_reason}{v.blocked_note ? ` — ${v.blocked_note}` : ""}</p>
            <p className="text-xs text-muted-foreground">Contact the owner, then reschedule below or put it back on the schedule.</p>
            <button data-testid="unblock-btn" onClick={() => act(() => api.post(`/visits/${id}/unblock`), "Back on schedule")} className="h-12 w-full rounded-md bg-primary font-bold text-primary-foreground">Put back on schedule</button>
          </div>
        )}

        <section data-testid="services-due">
          <p className="label-caps mb-2">Services due</p>
          <div className="card-flat divide-y divide-border">
            {v.services.length === 0 && <p className="p-4 text-sm text-muted-foreground">Add-on visit — no recurring services.</p>}
            {v.services.map((s, i) => (
              <div key={`${s.name}-${i}`} className="flex items-center justify-between p-4">
                <span className="font-semibold">{s.name} <span className="text-xs text-muted-foreground">({s.frequency})</span>
                  {s.billing_model === "monthly" && <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-secondary-foreground">monthly bill</span>}
                  {s.billing_model === "seasonal" && <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-secondary-foreground">seasonal pkg</span>}
                  {s.over_limit && <span data-testid="over-limit-badge" className="ml-2 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-bold text-destructive">over limit — bill manually</span>}
                </span>
                <span className="font-semibold">{s.billing_model === "seasonal" ? "installment" : fmtMoney(s.rate)}</span>
              </div>
            ))}
          </div>
        </section>

        {v.instructions.length > 0 && (
          <section data-testid="visit-instructions">
            <p className="label-caps mb-2">Special instructions</p>
            <div className="space-y-2">
              {v.instructions.map((i) => (
                <div key={i.id} className="card-flat border-l-4 border-l-[hsl(196,72%,56%)] p-4" data-testid={`instruction-${i.id}`}>
                  <p className="text-sm font-semibold">{i.description}</p>
                  {i.qa?.map((qa) => <p key={qa.q} className="mt-1 text-xs text-muted-foreground"><span className="font-semibold">{qa.q}</span> {qa.a}</p>)}
                  {i.quoted_cost != null && i.status === "approved" && <p className="mt-2 text-sm font-bold text-primary">Approved add-on · {fmtMoney(i.quoted_cost)}</p>}
                  {i.status === "awaiting_approval" && <p className="mt-2 text-sm font-bold text-[hsl(43,74%,40%)]">Awaiting owner approval · {fmtMoney(i.quoted_cost)} — completing this job is blocked until approved (or confirmed on the Add-ons page)</p>}
                  {["submitted", "clarifying"].includes(i.status) && <p className="mt-2 text-sm font-bold text-destructive">Not quoted yet — quote it on the Add-ons page before completing this job</p>}
                  {i.photo && <img src={photoSrc(i.photo)} alt="instruction" className="mt-2 h-24 rounded-md object-cover" />}
                </div>
              ))}
            </div>
          </section>
        )}

        <section data-testid="log-work-section">
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="log-work-btn" onClick={() => { setShowLogWork(!showLogWork); setShowLogIssue(false); }}
              className="h-12 rounded-md bg-secondary text-sm font-bold text-secondary-foreground">Log work request</button>
            <button data-testid="log-issue-btn" onClick={() => { setShowLogIssue(!showLogIssue); setShowLogWork(false); }}
              className="h-12 rounded-md bg-secondary text-sm font-bold text-secondary-foreground">Log an issue</button>
          </div>
          {showLogWork && (
            <div className="card-flat mt-2 p-4" data-testid="visit-log-work-panel">
              <LogWorkRequest propertyId={v.property_id} onDone={() => { setShowLogWork(false); load(); }} testid="visit-log-work" />
            </div>
          )}
          {showLogIssue && (
            <div className="card-flat mt-2 p-4" data-testid="visit-log-issue-panel">
              <LogIssue propertyId={v.property_id} visitId={v.id} onDone={() => { setShowLogIssue(false); load(); }} testid="visit-log-issue" />
            </div>
          )}
        </section>

        {v.offers.length > 0 && (
          <section data-testid="offer-status">
            <p className="label-caps mb-2">Dispatch offers</p>
            <div className="card-flat divide-y divide-border">
              {v.offers.map((o) => (
                <div key={o.id} className="flex items-center justify-between p-4 text-sm" data-testid={`offer-${o.id}`}>
                  <span className="font-semibold">{o.subcontractor_name}</span>
                  <span className={`font-bold capitalize ${o.status === "accepted" ? "text-primary" : o.status === "offered" ? "text-[hsl(43,74%,40%)]" : "text-muted-foreground"}`}>
                    {o.status}{o.decline_reason ? ` — ${o.decline_reason}` : ""}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {(v.check_in || v.check_out || v.crew?.some((c) => c.check_in || c.check_out)) && (
          <section className="card-flat space-y-2 p-4" data-testid="time-tracking">
            <p className="label-caps">Time on job</p>
            {v.check_in && <p className="flex items-center gap-2 text-sm"><MapPin size={16} weight="bold" /> In: {fmtTime(v.check_in.time)} <VerifyBadge stamp={v.check_in} /></p>}
            {v.check_out && <p className="flex items-center gap-2 text-sm"><MapPin size={16} weight="bold" /> Out: {fmtTime(v.check_out.time)} <VerifyBadge stamp={v.check_out} /></p>}
            {v.route_match && <p className="text-xs font-bold" data-testid="route-match-badge">Route check: {{ verified: "Verified", confirmed: "Confirmed", out_of_range: "Out of range", no_gps: "No GPS" }[v.route_match.status] || v.route_match.status}</p>}
            {v.evidence_level && <p className="text-xs font-bold" data-testid="evidence-level">Evidence: {v.evidence_level}</p>}
            {(v.completion || v.check_in) && (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/visits/${v.id}/map-snapshot`} alt="Map snapshot of the completion location"
                data-testid="map-snapshot" className="mt-2 w-full rounded-md border border-border"
                onError={(e) => { e.currentTarget.style.display = "none"; }} />
            )}
            {v.edits?.length > 0 && (
              <div className="space-y-1 border-t border-border pt-2" data-testid="visit-edits">
                <p className="label-caps">Edit history</p>
                {v.edits.map((e) => (
                  <p key={e.id} className="text-xs text-muted-foreground" data-testid={`visit-edit-${e.id}`}>{e.field}: {String(e.old_value ?? "—")} → {String(e.new_value ?? "—")} · {e.edited_by} · {fmtTime(e.edited_at)}</p>
                ))}
              </div>
            )}
            {v.crew?.map((c) => (c.check_in || c.check_out) && (
              <p key={c.id} className="flex items-center gap-2 text-sm" data-testid={`crew-time-${c.id}`}>
                <MapPin size={16} weight="bold" /> {c.name}: {c.check_in ? `in ${fmtTime(c.check_in.time)}` : "—"}{c.check_out ? ` → out ${fmtTime(c.check_out.time)}` : ""}
              </p>
            ))}
            {v.crew_costs && <p className="text-xs text-muted-foreground" data-testid="crew-costs">Crew pay: {Object.values(v.crew_costs).map((c) => `${c.name} ${fmtMoney(c.cost)} (${c.hours}h)`).join(" · ")}</p>}
          </section>
        )}

        {workable && (
          <section className="space-y-3">
            {sig !== undefined && (
              <p data-testid="signal-indicator" className="text-sm font-semibold">Signal: {sig === null ? "no fix — saved without GPS" : signalLabel(sig)}</p>
            )}
            {!v.check_in && (
              <button data-testid="check-in-btn" onClick={() => geoAction("checkin", "Checked in")} className="btn-primary-big">Check in on site</button>
            )}
            {!v.check_in && (
              <button data-testid="set-pin-here-btn" onClick={setPinHere} className="h-14 w-full rounded-md border-2 border-border text-sm font-bold">Set pin here — I'm standing at the service spot</button>
            )}
            {v.check_in && !v.check_out && v.status === "in_progress" && (
              <button data-testid="check-out-btn" onClick={() => geoAction("checkout", "Checked out")} className="h-14 w-full rounded-md border-2 border-primary text-lg font-bold text-primary transition-colors hover:bg-primary/5">Check out</button>
            )}
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="assign-self-btn" onClick={() => act(() => api.post(`/visits/${id}/assign-self`), "Assigned to you")} className="h-14 rounded-md bg-secondary font-bold text-secondary-foreground">I'll do it</button>
              <button data-testid="dispatch-btn" onClick={() => setShowDispatch(!showDispatch)} className="h-14 rounded-md bg-secondary font-bold text-secondary-foreground">Send to crew</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button data-testid="delay-1-btn" onClick={() => act(() => api.post(`/visits/${id}/delay`, { days: 1, reason: "rain" }), "Delayed to tomorrow")} className="h-14 rounded-md border-2 border-[hsl(196,72%,56%)] font-bold text-[hsl(196,72%,46%)]">Rain delay +1 day</button>
              <button data-testid="delay-2-btn" onClick={() => act(() => api.post(`/visits/${id}/delay`, { days: 2, reason: "rain" }), "Delayed 2 days")} className="h-14 rounded-md border-2 border-[hsl(196,72%,56%)] font-bold text-[hsl(196,72%,46%)]">Rain delay +2 days</button>
            </div>
            <button data-testid="reschedule-btn" onClick={() => setShowResched(!showResched)} className="h-14 w-full rounded-md border border-border font-bold">Reschedule to another date</button>
            {showResched && (
              <div className="card-flat space-y-2 p-4" data-testid="reschedule-form">
                <input data-testid="reschedule-date" type="date" className="field-input w-full" value={resched.date} onChange={(e) => setResched({ ...resched, date: e.target.value })} />
                <input data-testid="reschedule-reason" className="field-input w-full" placeholder="Reason (crew availability, owner request…)" value={resched.reason} onChange={(e) => setResched({ ...resched, reason: e.target.value })} />
                <button data-testid="reschedule-save" onClick={() => act(async () => { await api.post(`/visits/${id}/reschedule`, resched); setShowResched(false); }, "Job moved — crew notified")} disabled={!resched.date} className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground disabled:opacity-40">Move job</button>
              </div>
            )}

            {showDispatch && (
              <div className="card-flat space-y-4 p-4" data-testid="dispatch-panel">
                <p className="text-sm font-semibold">Tap crew to offer them the job — first to accept gets it.</p>
                {v.crew_suggestions?.length > 0 && (
                  <div className="space-y-2 rounded-md bg-muted p-3" data-testid="crew-suggestions">
                    <p className="text-xs font-bold text-muted-foreground">Suggested for this job:</p>
                    {v.crew_suggestions.map((sug) => (
                      <button key={sug.id} data-testid={`suggest-${sug.id}`} onClick={() => setPicked((p) => p.includes(sug.id) ? p : [...p, sug.id])}
                        className={`w-full rounded-md border p-3 text-left text-sm transition-colors ${picked.includes(sug.id) ? "border-primary bg-primary/10" : "border-border bg-card"}`}>
                        <span className="font-semibold">{sug.name}</span>
                        {!sug.available && <span className="ml-2 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-bold text-destructive">off that day</span>}
                        <span className="block text-xs text-muted-foreground">{sug.reasons.join(" · ")}</span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="space-y-2">
                  {subs.filter((s) => s.status === "active").map((s) => (
                    <button key={s.id} data-testid={`pick-sub-${s.id}`} onClick={() => setPicked((p) => p.includes(s.id) ? p.filter((x) => x !== s.id) : [...p, s.id])}
                      className={`flex w-full items-center justify-between rounded-md border p-4 text-left transition-colors ${picked.includes(s.id) ? "border-primary bg-primary/5" : "border-border"}`}>
                      <div>
                        <p className="font-semibold">{s.name} {s.id === v.preferred_contractor_id && <Star size={14} weight="fill" className="inline text-[hsl(43,74%,50%)]" />}
                          {v.crew_availability?.[s.id] && !v.crew_availability[s.id].available && <span className="ml-2 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-bold text-destructive" data-testid={`unavailable-${s.id}`}>{v.crew_availability[s.id].note || "off that day"}</span>}
                        </p>
                        <p className="text-xs text-muted-foreground">{s.service_types.join(", ")} · {fmtMoney(s.rate)}/{s.rate_basis === "hourly" ? "hr" : "visit"}</p>
                        {s.doc_warnings?.length > 0 && <p className="mt-1 flex items-center gap-1 text-xs font-bold text-destructive"><Warning size={12} weight="bold" /> {s.doc_warnings.join(" · ")}</p>}
                      </div>
                    </button>
                  ))}
                </div>
                <button data-testid="send-offers-btn" onClick={dispatch} disabled={picked.length === 0} className="btn-primary-big !h-14 !text-base disabled:opacity-40">Send offers</button>
                <button data-testid="assign-direct-btn" onClick={assignDirect} disabled={picked.length !== 1}
                  className="h-12 w-full rounded-md border border-primary text-sm font-bold text-primary transition-colors hover:bg-primary/5 disabled:opacity-40">
                  Assign directly{picked.length === 1 ? ` to ${subs.find((s) => s.id === picked[0])?.name || "selected crew"}` : ""} — no offer, it's theirs
                </button>
                {teams.length > 0 && (
                  <div className="space-y-2 border-t border-border pt-3" data-testid="team-dispatch">
                    <p className="text-xs font-bold text-muted-foreground">Teams — assign instantly, no offers:</p>
                    {teams.map((t) => (
                      <button key={t.id} data-testid={`pick-team-${t.id}`} onClick={() => act(() => api.post(`/visits/${id}/dispatch`, { mode: "broadcast", team_id: t.id }), `Team ${t.name} assigned`)}
                        className="w-full rounded-md border border-border p-4 text-left transition-colors hover:bg-muted/50">
                        <p className="font-semibold">{t.name}</p>
                        <p className="text-xs text-muted-foreground">{(t.members || []).map((m) => m.name).join(" + ")}</p>
                      </button>
                    ))}
                  </div>
                )}
                <details className="text-sm" data-testid="dispatch-advanced">
                  <summary className="cursor-pointer text-xs font-bold text-muted-foreground">Offer options (one-at-a-time, response window)</summary>
                  <div className="mt-3 space-y-3">
                    <div className="flex gap-2">
                      {["broadcast", "sequential"].map((m) => (
                        <button key={m} data-testid={`mode-${m}`} onClick={() => setMode(m)}
                          className={`h-12 flex-1 rounded-md border text-sm font-bold capitalize transition-colors ${mode === m ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{m === "broadcast" ? "All at once" : "One at a time"}</button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">{mode === "sequential" ? "Offered one at a time; next in line if declined." : "Offered to all at once; first to accept takes it."}</p>
                    <label className="block text-sm font-semibold">Response window (minutes)
                      <input data-testid="response-minutes" type="number" className="field-input mt-1 w-full" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
                    </label>
                  </div>
                </details>
              </div>
            )}

            {v.status === "in_progress" && (
              <div className="card-flat space-y-3 p-4" data-testid="complete-panel">
                <p className="label-caps">Wrap up</p>
                <textarea data-testid="complete-notes" className="field-input h-24 w-full py-3" placeholder="Notes for the record (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
                <PhotoPicker photos={photos} setPhotos={setPhotos} min={v.min_completion_photos ?? 2} testid="add-photo-btn" cameraOnly={!!v.event_id} />
                {v.upsell_catalog?.length > 0 && (
                  <div data-testid="upsell-picker">
                    <p className="label-caps mb-2">Add-ons performed (billed to owner)</p>
                    <div className="flex flex-wrap gap-2">
                      {v.upsell_catalog.map((a, i) => {
                        const on = upsells.some((u) => u.name === a.name);
                        return (
                          <button key={`${a.name}-${i}`} type="button" data-testid={`upsell-${i}`}
                            onClick={() => setUpsells((u) => (on ? u.filter((x) => x.name !== a.name) : [...u, { name: a.name, price: a.price }]))}
                            className={`h-12 rounded-md border px-4 text-sm font-bold transition-colors ${on ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                            {a.name} · {fmtMoney(a.price)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <button data-testid="complete-btn" onClick={complete} disabled={photos.length < (v.min_completion_photos ?? 2)} className="btn-primary-big !h-14 !text-base disabled:opacity-40">Mark complete</button>
              </div>
            )}

            {["scheduled", "assigned"].includes(v.status) && (
              <div className="card-flat space-y-3 p-4" data-testid="behalf-complete-panel">
                <p className="label-caps">Complete on behalf of crew</p>
                <p className="text-xs text-muted-foreground">Crew finished but didn't log it? Record their time on site, photos and notes — hourly crew pay and the invoice draft work as usual.</p>
                {v.assignee_rate_basis === "hourly" && <p className="rounded-md bg-muted p-2 text-xs font-bold" data-testid="behalf-hourly-hint">Hourly crew member — the minutes you enter drive their pay.</p>}
                <input data-testid="behalf-minutes" type="number" min="1" className="field-input w-full" placeholder="Minutes on site (e.g. 45)" value={minutesOnSite} onChange={(e) => setMinutesOnSite(e.target.value)} />
                <textarea data-testid="behalf-notes" className="field-input h-20 w-full py-3" placeholder="Notes for the record (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
                <PhotoPicker photos={photos} setPhotos={setPhotos} min={0} testid="behalf-add-photo-btn" />
                <button data-testid="behalf-complete-btn" onClick={completeForCrew} disabled={!Number(minutesOnSite)} className="btn-primary-big !h-14 !text-base disabled:opacity-40">Complete for crew</button>
              </div>
            )}
          </section>
        )}

        {v.status === "cancelled" && (
          <div className="card-flat border-l-4 border-l-destructive p-4" data-testid="job-cancelled-banner">
            <p className="font-bold text-destructive">Cancelled</p>
            <p className="text-sm text-muted-foreground">{v.cancel_reason}{v.cancelled_by ? ` — by ${v.cancelled_by}` : ""}</p>
          </div>
        )}

        {!["invoiced", "paid", "cancelled"].includes(v.status) && (
          <ExpensePanel visitId={id} expenses={v.expenses || []} receiptRequired={v.receipt_required} canRemove onDone={load} testid="expense" />
        )}

        {cancellable && (
          <section data-testid="cancel-job-section">
            {!showCancel ? (
              <button data-testid="cancel-job-btn" onClick={() => setShowCancel(true)}
                className="h-12 w-full rounded-md border border-destructive text-sm font-bold text-destructive">Cancel this job</button>
            ) : (
              <div className="card-flat space-y-2 border-destructive/40 p-4" data-testid="cancel-job-form">
                <p className="text-xs text-muted-foreground">The job comes off the schedule, open crew offers are withdrawn, any draft invoice is cancelled, and assigned crew get a text. The reason is logged.</p>
                <input data-testid="cancel-job-reason" className="field-input w-full" placeholder="Reason (e.g. already done by someone else)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} />
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="cancel-job-back" onClick={() => setShowCancel(false)} className="h-12 rounded-md border border-border text-sm font-bold">Keep job</button>
                  <button data-testid="cancel-job-confirm" onClick={cancelJob} disabled={!cancelReason.trim()}
                    className="h-12 rounded-md bg-destructive text-sm font-bold text-destructive-foreground disabled:opacity-40">Cancel job</button>
                </div>
              </div>
            )}
          </section>
        )}

        {v.invoice_id && (
          <Link to={`/invoices/${v.invoice_id}`} data-testid="view-invoice-link" className="card-flat flex items-center justify-between p-4 font-semibold text-primary">
            View invoice <span>→</span>
          </Link>
        )}
        {v.completion_notes && <div className="card-flat p-4 text-sm"><p className="label-caps mb-1">Completion notes</p>{v.completion_notes}</div>}
        {v.photos?.length > 0 && <div className="flex gap-2">{v.photos.map((p) => <img key={p} src={photoSrc(p)} alt="completion" className="h-20 w-20 rounded-md object-cover" />)}</div>}
      </div>
    </Layout>
  );
}
